/**
 * A serialised write queue with a monotonic "superseded" guard.
 *
 * Problem this solves (regression run 2026-07-12/13, R1 EXCEPTION 1 —
 * IntakeFormV2's autosave race): several async writes can be requested in
 * quick succession (a debounced autosave, "Next", "Submit", a
 * beforeunload/pagehide flush). If those writes are allowed to run
 * concurrently, network jitter can let an OLDER write's response land AFTER
 * a NEWER write's — silently reverting whatever the newer write just
 * persisted, with no visible error to the user (confirmed via SQL: the final
 * on-screen state was correct, but the row that actually landed in the
 * database was the earlier, superseded one).
 *
 * This queue guarantees two things:
 *  1. At most one `run` is ever executing at a time (strict FIFO
 *     serialisation) — so there is never more than one in-flight write to
 *     race against another.
 *  2. `schedule()` tells the caller whether a NEWER call had already been
 *     issued by the time this one finished (`wasSuperseded`), so the caller
 *     can skip "this is now the saved state" bookkeeping for a write that a
 *     later one has already overtaken — the last-issued write always wins.
 *
 * Deliberately framework-free (no React) so it is trivially unit-testable
 * and reusable anywhere the same "debounce + flush, never let an older
 * write clobber a newer one" shape shows up.
 */

export interface WriteResult<T> {
  /** Return value of the `run` callback, or `undefined` if it threw. */
  value: T | undefined;
  /** True when a newer `schedule()` call was issued before this one's `run`
   *  finished executing. The caller should treat a superseded result as
   *  "don't trust this as the latest truth" — the newer call's own result is
   *  authoritative instead. */
  wasSuperseded: boolean;
  /** The error `run` threw, if any. Queue continues regardless — one failed
   *  write never wedges writes queued behind it. */
  error: unknown;
}

export interface SerializedWriteQueue {
  /**
   * Enqueue `run`. Resolves once `run` has executed in its turn (strictly
   * after every previously-scheduled `run` has settled, success or not).
   */
  schedule<T>(run: () => Promise<T>): Promise<WriteResult<T>>;
  /** Number of schedule() calls issued so far. Exposed for test assertions. */
  readonly sequence: number;
}

export function createSerializedWriteQueue(): SerializedWriteQueue {
  let tail: Promise<unknown> = Promise.resolve();
  let seq = 0;

  return {
    get sequence() {
      return seq;
    },
    schedule<T>(run: () => Promise<T>): Promise<WriteResult<T>> {
      const mySeq = ++seq;
      const settled: Promise<WriteResult<T>> = tail.then(
        async (): Promise<WriteResult<T>> => {
          try {
            const value = await run();
            return { value, wasSuperseded: mySeq !== seq, error: undefined };
          } catch (error) {
            return { value: undefined, wasSuperseded: mySeq !== seq, error };
          }
        },
      );
      // The next scheduled write waits on `settled`, not on `run` directly,
      // so a thrown error can never wedge the queue for writes behind it.
      tail = settled;
      return settled;
    },
  };
}
